import type { CostCategory, EventData } from './types';

export type RevenueRange = {
  min: number;
  max: number;
};

export type Totals = {
  ticketRevenue: RevenueRange;
  sponsorRevenue: RevenueRange;
  addonRevenue: number;
  revenue: RevenueRange;
  costs: number;
  costsByCategory: Record<CostCategory, number>;
  net: RevenueRange;
  ticketCount: number;
  sponsorCount: number;
  addonCount: number;
  // Unit economics — derived ratios for YoY comparisons and sponsor convos.
  // Both are zero when ticketCount === 0 (UI hides the row in that case).
  perAttendee: {
    revenue: RevenueRange;
    cost: number;
  };
};

export function calculate(data: EventData): Totals {
  const ticketRevenue = data.tickets.reduce<RevenueRange>(
    (acc, t) => ({
      min: acc.min + t.qty * t.priceMin,
      max: acc.max + t.qty * t.priceMax,
    }),
    { min: 0, max: 0 },
  );
  const ticketCount = data.tickets.reduce((acc, t) => acc + t.qty, 0);

  const sponsorRevenue = data.sponsors.reduce<RevenueRange>(
    (acc, s) => ({
      min: acc.min + s.qty * s.priceMin,
      max: acc.max + s.qty * s.priceMax,
    }),
    { min: 0, max: 0 },
  );
  const sponsorCount = data.sponsors.reduce((acc, s) => acc + s.qty, 0);

  const addonRevenue = data.addons.reduce((acc, a) => {
    if (!a.enabled) return acc;
    const mult = a.multiplier && a.multiplier > 0 ? a.multiplier : 1;
    return acc + a.price * mult;
  }, 0);
  const addonCount = data.addons.filter((a) => a.enabled).length;

  const costsByCategory = (Object.keys(data.costs) as CostCategory[]).reduce(
    (acc, key) => {
      acc[key] = data.costs[key].reduce((s, item) => s + item.amount, 0);
      return acc;
    },
    {} as Record<CostCategory, number>,
  );
  const costs = (Object.values(costsByCategory) as number[]).reduce(
    (a, b) => a + b,
    0,
  );

  const revenue: RevenueRange = {
    min: ticketRevenue.min + sponsorRevenue.min + addonRevenue,
    max: ticketRevenue.max + sponsorRevenue.max + addonRevenue,
  };
  const net: RevenueRange = {
    min: revenue.min - costs,
    max: revenue.max - costs,
  };

  const perAttendee =
    ticketCount > 0
      ? {
          revenue: {
            min: revenue.min / ticketCount,
            max: revenue.max / ticketCount,
          },
          cost: costs / ticketCount,
        }
      : { revenue: { min: 0, max: 0 }, cost: 0 };

  return {
    ticketRevenue,
    sponsorRevenue,
    addonRevenue,
    revenue,
    costs,
    costsByCategory,
    net,
    ticketCount,
    sponsorCount,
    addonCount,
    perAttendee,
  };
}
